// routes/_app.$usernameRaw._index.tsx
//
// `/{lang}/@username` — the bare profile root. Has no rendered content
// of its own: the parent layout (`clientRoutes/_app.$usernameRaw.tsx`)
// always `<Navigate to="artworks" replace />` whenever the pathname
// is exactly `/@username`, so the only thing this route ever produced
// in CSR mode was a one-frame shell flash before the SPA redirect
// fired.
//
// Pre-change behaviour was the worst-of-both-worlds variant: the
// `dynamic({ssr:false})` real component lived behind a `<Suspense
// fallback={<UserProfileShell ...>}>` while a duplicate `<UserProfileShell>`
// rendered for the non-hydrated path. SSR HTML therefore shipped a
// chrome shell with fake avatar / follower counts that *never*
// matched the real `<UserProfileContent>` markup the client mounted
// after the SPA redirect to `/artworks`. Google saw the shell HTML,
// users saw a flash, and the loader did a full `fetchUserForSSR` +
// `getSeoConfig` round-trip just to feed that shell.
//
// New behaviour: the loader responds with a `302` straight to
// `/{lang}/@username/artworks`. Search crawlers follow the redirect
// to the real artworks page (whose loader does the actual SEO meta
// work and ships a fully populated grid); users skip the shell flash
// entirely; we save one redundant `fetchUserForSSR` per visit.
import { redirect } from 'react-router'
import { userProfileHandle, userProfileLoader } from 'src/utils/router/route'
import type { Route } from './+types/_app.$usernameRaw._index'

export const handle = userProfileHandle

/**
 * Build the canonical `/{lang}/@username/artworks` URL, preserving
 * any incoming `?query` and `#hash` so deep-links keep working
 * (e.g. `/zh/@user?foo=bar#tab=models` → `/zh/@user/artworks?foo=bar#tab=models`).
 */
function buildArtworksRedirectUrl(request: Request): string {
  const url = new URL(request.url)
  const cleanPath = url.pathname.replace(/\/$/, '')
  return `${cleanPath}/artworks${url.search}${url.hash}`
}

export async function loader({ request }: Route.LoaderArgs) {
  throw redirect(buildArtworksRedirectUrl(request))
}

export async function clientLoader({
  request,
  params,
}: Route.ClientLoaderArgs) {
  // Keep the legacy `userProfileLoader` (handles `@` prefix validation
  // and a couple of `likes/liked-*` legacy-tab redirects) in the
  // client codepath so SPA navigations into `/@user` still get the
  // same redirect contract as a fresh load. Then throw the canonical
  // 302 ourselves — `userProfileLoader` doesn't redirect the root
  // path on its own.
  await userProfileLoader({ request, params })
  throw redirect(buildArtworksRedirectUrl(request))
}
clientLoader.hydrate = true

/**
 * Placeholder element — never actually mounted because both `loader`
 * and `clientLoader` `throw redirect(...)` before React Router gets a
 * chance to render this route. The CSR data router
 * (`src/data-router/routes.tsx`) still references `module.default` as
 * the route's `element`, so we have to export *something*.
 */
export default function UserProfileIndexRedirect() {
  return null
}
